import { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useConfig } from '@/contexts/ConfigContext';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/services/auth';
import { adminAuthService } from '@/services/adminAuth';
import { LogIn, AlertCircle, Settings } from 'lucide-react';

export function Login() {
  const { isAuthenticated, login, isLoading } = useAuth();
  const { config } = useConfig();
  const { resolvedTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [ssoStatus, setSsoStatus] = useState<{
    isLoading: boolean;
    ssoConfigured: boolean;
    ssoProvider: string | null;
  }>({
    isLoading: true,
    ssoConfigured: true, // Assume configured until we know otherwise
    ssoProvider: null,
  });

  // Get the redirect URL from state, or default to home
  const from = (location.state as any)?.from?.pathname || '/';

  // Check SSO configuration status
  useEffect(() => {
    const checkSsoStatus = async () => {
      try {
        const authConfig = await adminAuthService.getAuthConfig();
        setSsoStatus({
          isLoading: false,
          ssoConfigured: authConfig.sso_configured,
          ssoProvider: authConfig.sso_provider,
        });
      } catch (error) {
        console.error('Failed to check SSO status:', error);
        setSsoStatus({
          isLoading: false,
          ssoConfigured: false,
          ssoProvider: null,
        });
      }
    };

    checkSsoStatus();
  }, []);

  useEffect(() => {
    // If already authenticated, redirect
    if (isAuthenticated && !isLoading) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, from]);

  const handleLogin = () => {
    // Clear any previous logout flag
    authService.clearLogoutFlag();
    login();
  };

  const logoUrl = resolvedTheme === 'dark' ? config.logo_dark_url : config.logo_light_url;

  if (isLoading || ssoStatus.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Show "SSO not configured" message if SSO isn't set up
  if (!ssoStatus.ssoConfigured) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={config.portal_name || 'Product Portal'}
                className="mx-auto h-16 w-auto"
              />
            ) : (
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {config.portal_name || 'Product Portal'}
              </h1>
            )}
          </div>

          <div className="bg-white dark:bg-gray-800 py-8 px-6 shadow-lg rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="text-center">
              <div className="mx-auto w-12 h-12 bg-yellow-100 dark:bg-yellow-900/50 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                SSO Not Configured
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Single Sign-On has not been configured for this application.
                Please contact your administrator to set up SSO.
              </p>

              <Link
                to="/admin-setup"
                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                <Settings className="mr-2 h-4 w-4" />
                Admin Setup
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={config.portal_name || 'Product Portal'}
              className="mx-auto h-16 w-auto"
            />
          ) : (
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {config.portal_name || 'Product Portal'}
            </h1>
          )}
          <h2 className="mt-6 text-xl font-semibold text-gray-900 dark:text-white">
            Sign in to your account
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Use your organization credentials to access the portal
          </p>
        </div>

        <div className="mt-8 bg-white dark:bg-gray-800 py-8 px-6 shadow-lg rounded-lg border border-gray-200 dark:border-gray-700">
          <button
            onClick={handleLogin}
            className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
          >
            <LogIn className="mr-2 h-5 w-5" />
            Sign in with SSO
          </button>

          <p className="mt-4 text-center text-xs text-gray-500 dark:text-gray-400">
            By signing in, you agree to the terms of service and privacy policy.
          </p>
        </div>
      </div>
    </div>
  );
}
